import { useCallback, useEffect, useState } from 'react';
import {
  Box, Paper, Typography, Breadcrumbs, Link as MuiLink, Table, TableHead, TableRow,
  TableCell, TableBody, TableContainer, Chip, TextField, Tooltip, LinearProgress,
} from '@mui/material';
import SecurityIcon from '@mui/icons-material/Security';
import RuleIcon from '@mui/icons-material/Rule';
import { useParams, useNavigate, Link as RouterLink } from 'react-router-dom';
import { motion } from 'motion/react';
import { pageVariants, sectionVariants } from '../../../animations/variants';
import { getStandardControls } from '../../../services/securityhubApi';
import { standardLabel } from './standards';
import { severityChipSx } from './severity';
import { formatStatusLabel } from '../../../utils/statusLabel';
import PortalPageHeader from '../../../components/portal/PortalPageHeader';
import {
  PortalLoading, PortalErrorState, PortalSectionHeader,
} from '../../../components/portal/PortalStates';
import {
  statusChipSx, intentFromColor, tableHeadRowSx, headCellSx, rowSx, bodyCellSx,
  sectionCardSx, sectionHeadingSx, emptyTextSx, actionRowSx,
} from '../portalStyles';

const STATUS_COLOR = { PASSED: 'success', FAILED: 'error', UNKNOWN: 'warning', NO_DATA: 'default' };
const STATUS_LABEL = { PASSED: 'Passed', FAILED: 'Failed', UNKNOWN: 'Unknown', NO_DATA: 'No data' };
const FILTERS = ['', 'FAILED', 'PASSED', 'UNKNOWN', 'NO_DATA'];
const FILTER_LABEL = { '': 'All', FAILED: 'Failed', PASSED: 'Passed', UNKNOWN: 'Unknown', NO_DATA: 'No data' };

export default function StandardDetail() {
  const { standardId } = useParams();
  const navigate = useNavigate();
  const decoded = decodeURIComponent(standardId);
  const [controls, setControls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [status, setStatus] = useState('');
  const [search, setSearch] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getStandardControls(decoded, { status, search });
      setControls(res?.data || []);
      setError(null);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [decoded, status, search]);
  useEffect(() => { load(); }, [load]);

  const counts = controls.reduce((a, c) => { a[c.controlStatus] = (a[c.controlStatus] || 0) + 1; return a; }, {});

  return (
    <motion.div variants={pageVariants} initial="initial" animate="animate">
      <motion.div variants={sectionVariants}>
      <Breadcrumbs sx={{ mb: 1.5 }}>
        <MuiLink component={RouterLink} to="/securityhub/cspm" underline="hover">Security Hub CSPM</MuiLink>
        <MuiLink component={RouterLink} to="/securityhub/cspm/standards" underline="hover">Security standards</MuiLink>
        <Typography color="text.primary">{standardLabel(decoded)}</Typography>
      </Breadcrumbs>
      <PortalPageHeader
        icon={<SecurityIcon sx={{ fontSize: 24 }} />}
        title={standardLabel(decoded)}
        subtitle="Control-by-control results for this Security Hub standard"
      />
      </motion.div>

      {error && (
        <motion.div variants={sectionVariants}>
          <PortalErrorState error={error} onRetry={load} />
        </motion.div>
      )}

      <motion.div variants={sectionVariants}>
      <Paper elevation={0} sx={{ ...sectionCardSx, mb: 3 }}>
        <Typography sx={{ ...sectionHeadingSx, mb: 1 }}>Control status</Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
          <Chip label={`${counts.PASSED || 0} Passed`} size="small" sx={statusChipSx('success')} />
          <Chip label={`${counts.FAILED || 0} Failed`} size="small" sx={statusChipSx('error')} />
          <Chip label={`${counts.UNKNOWN || 0} Unknown`} size="small" sx={statusChipSx('warning')} />
          <Chip label={`${counts.NO_DATA || 0} No data`} size="small" sx={statusChipSx('neutral')} />
        </Box>
      </Paper>
      </motion.div>

      <motion.div variants={sectionVariants}>
      <Paper elevation={0} sx={sectionCardSx}>
        <PortalSectionHeader
          icon={<RuleIcon sx={{ fontSize: 20 }} />}
          title={`All controls (${controls.length})`}
          subtitle="Filter by status or search a control by ID or title"
          mb={2}
        />
        <Box sx={{ ...actionRowSx, mb: 2 }}>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {FILTERS.map((f) => (
              <Chip
                key={f || 'all'}
                label={FILTER_LABEL[f]}
                size="small"
                onClick={() => setStatus(f)}
                sx={{
                  ...statusChipSx(status === f ? 'info' : 'neutral'),
                  cursor: 'pointer',
                  transition: 'background-color 0.2s ease, border-color 0.2s ease',
                }}
              />
            ))}
          </Box>
          <TextField size="small" label="Search controls" value={search}
            onChange={(e) => setSearch(e.target.value)} sx={{ minWidth: 0, flex: '1 1 220px', maxWidth: 340 }} />
        </Box>
        {loading && controls.length > 0 && <LinearProgress sx={{ mb: 1.5, borderRadius: 1 }} />}
        {loading && controls.length === 0 ? (
          <PortalLoading label="Loading controls…" />
        ) : (
          <TableContainer sx={{ overflowX: 'auto' }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={tableHeadRowSx}>
                  <TableCell sx={headCellSx}>ID</TableCell>
                  <TableCell sx={headCellSx}>Title</TableCell>
                  <TableCell sx={headCellSx}>Control status</TableCell>
                  <TableCell sx={headCellSx}>Severity</TableCell>
                  <TableCell sx={headCellSx}>Failed checks</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {controls.map((c, i) => (
                  <TableRow key={c.securityControlId} hover sx={{ ...rowSx(i), cursor: 'pointer' }}
                    onClick={() => navigate(`/securityhub/cspm/standards/${encodeURIComponent(decoded)}/controls/${encodeURIComponent(c.securityControlId)}`)}>
                    <TableCell sx={bodyCellSx}>{c.securityControlId}</TableCell>
                    <TableCell sx={{ ...bodyCellSx, maxWidth: 460, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      <Tooltip title={c.title || ''}>
                        <span>{c.title}</span>
                      </Tooltip>
                    </TableCell>
                    <TableCell sx={bodyCellSx}><Chip size="small" label={STATUS_LABEL[c.controlStatus] || formatStatusLabel(c.controlStatus)} sx={statusChipSx(intentFromColor(STATUS_COLOR[c.controlStatus] || 'default'))} /></TableCell>
                    <TableCell sx={bodyCellSx}><Chip size="small" label={c.severityRating} sx={severityChipSx(c.severityRating)} /></TableCell>
                    <TableCell sx={bodyCellSx}>{c.failedChecks} of {c.totalChecks}</TableCell>
                  </TableRow>
                ))}
                {!loading && controls.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={5} align="center" sx={{ py: 4, borderBottom: 'none' }}>
                      <Typography sx={emptyTextSx}>
                        No controls match this filter — clear the search or pick another status.
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>
      </motion.div>
    </motion.div>
  );
}
